// utils/fuseSearch.js
import Fuse from 'fuse.js';

/**
 * Creates a Fuse.js instance configured for Polish language search
 * 
 * @param {Array} items - The array of items to search through
 * @param {Object} options - Custom Fuse.js options to override defaults
 * @returns {Fuse} - Configured Fuse instance
 */
export function createFuseInstance(items, options = {}) {
    // Default options optimized for Polish language
    const defaultOptions = {
        keys: [
            { name: 'title', weight: 0.7 },
            { name: 'content', weight: 0.5 },
            { name: 'excerpt', weight: 0.3 }
        ],
        includeScore: true,
        threshold: 0.4,
        ignoreLocation: true,  // Important for inflected languages
        useExtendedSearch: true,
        distance: 100,
        minMatchCharLength: 2,
        shouldSort: true
    };

    // Merge default options with custom options
    const fuseOptions = { ...defaultOptions, ...options };
    console.log(fuseOptions);
    return new Fuse(items, fuseOptions);
}

/**
 * Preprocesses items to enhance search capabilities for Polish language
 * 
 * @param {Array} items - Original items array
 * @param {Object} config - Configuration for preprocessing
 * @returns {Array} - Enhanced items array
 */
export function preprocessItems(items, config = {}) {
    const {
        tagField = 'tags',
        tagNameField = 'name',
        includeTagString = true
    } = config;
    
    return items.map(item => {
        const enhancedItem = { ...item };
        
        // Add concatenated tag string for better tag searching
        if (includeTagString && item[tagField] && Array.isArray(item[tagField])) {
            enhancedItem.tagString = item[tagField]
                .map(tag => tag[tagNameField])
                .join(' ');
        }
     
        return enhancedItem;
    });
}

/**
 * Performs a search using Fuse.js and handles additional filtering
 * 
 * @param {Fuse} fuse - Fuse.js instance
 * @param {string} searchTerm - Search term
 * @param {Object} additionalFilters - Additional filter functions to apply
 * @returns {Array} - Filtered search results
 */
export function performSearch(fuse, searchTerm, additionalFilters = {}) {
    if (!searchTerm || !fuse) {
        // If no search term, return all items with additional filters applied
        const items = fuse.getIndex().docs;
        return filterItems(items, additionalFilters);
    }
    
    // Perform Fuse.js search
    const searchResults = fuse.search(searchTerm);
    
    // Extract items and apply additional filters
    const filteredResults = searchResults
        .map(result => result.item)
        .filter(item => applyFilters(item, additionalFilters));
        
    return filteredResults;
}

/**
 * Applies additional filters to an array of items
 * 
 * @param {Array} items - Items to filter
 * @param {Object} filters - Filter functions to apply
 * @returns {Array} - Filtered items
 */
function filterItems(items, filters) {
    return items.filter(item => applyFilters(item, filters));
}

/**
 * Applies all filters to a single item
 * 
 * @param {Object} item - Item to check
 * @param {Object} filters - Map of filter functions
 * @returns {boolean} - Whether the item passes all filters
 */
function applyFilters(item, filters) {
    for (const [filterName, filterFn] of Object.entries(filters)) {
        if (typeof filterFn === 'function' && !filterFn(item)) {
            return false;
        }
    }
    return true;
}

/**
 * Creates a year filter function
 * 
 * @param {string} year - Year to filter by
 * @param {string} fieldName - Field name containing year value
 * @returns {Function} - Filter function
 */
export function createYearFilter(year, fieldName = 'publication_year') {
    if (year === 'all') return () => true;
    return (item) => item[fieldName] === year;
}

/**
 * Creates a tag filter function
 * 
 * @param {string} tagName - Tag name to filter by
 * @param {string} tagsField - Field name containing tags array
 * @param {string} tagNameField - Field in tag object containing name
 * @returns {Function} - Filter function
 */
export function createTagFilter(tagName, tagsField = 'tags', tagNameField = 'name') {
    if (!tagName) return () => true;
    return (item) => {
        return item[tagsField] && 
               Array.isArray(item[tagsField]) && 
               item[tagsField].some(tag => 
                   tag[tagNameField].toLowerCase() === tagName.toLowerCase()
               );
    };
}